import { api } from "../api";
import { openRecipeSheet } from "../components/recipeSheet";
import { msg } from "../messages";
import { isNavigationStale } from "../navigation";
import type {
    DashboardData,
    DashboardGreeting,
    ExpiringItemView,
    RecipeSuggestion,
} from "../types/dashboard";
import { formatDiaryDate } from "../utils/dates";
import { createIcon } from "../utils/icons";
import { resolveCachedImageSrc } from "../utils/images";
import { formatIngredientExpiryLabel, getIngredientExpiryStatus } from "../utils/recipeIngredients";

let dashboardData: DashboardData | null = null;

interface EditorialCountdown {
    value: string;
    unit: string;
    expired: boolean;
}

function formatEditorialCountdown(daysLeft: number | null): EditorialCountdown {
    if (daysLeft === null) return { value: "—", unit: "", expired: false };
    if (daysLeft < 0) {
        const days = Math.abs(daysLeft);
        return {
            value: String(days),
            unit: days === 1 ? "day ago" : "days ago",
            expired: true,
        };
    }
    if (daysLeft === 0) return { value: "0", unit: "today", expired: false };
    if (daysLeft === 1) return { value: "1", unit: "day left", expired: false };
    return { value: String(daysLeft), unit: "days left", expired: false };
}

function renderIngredientsLine(
    recipe: RecipeSuggestion,
    expiringItems: ExpiringItemView[],
): DocumentFragment {
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < recipe.ingredients.length; i++) {
        if (i > 0) {
            const sep = document.createElement("span");
            sep.className = "recipe-ingredient-sep";
            sep.textContent = ", ";
            fragment.appendChild(sep);
        }
        const ing = recipe.ingredients[i];
        const span = document.createElement("span");
        span.className = "recipe-ingredient";
        const expiryStatus = getIngredientExpiryStatus(ing, expiringItems);
        if (expiryStatus === "expired") {
            span.classList.add("recipe-ingredient--expired");
        } else if (expiryStatus === "expiring") {
            span.classList.add("recipe-ingredient--expiring");
        }
        span.textContent = formatIngredientExpiryLabel(ing, expiringItems);
        fragment.appendChild(span);
    }
    return fragment;
}

function getExpiringItems(): ExpiringItemView[] {
    return dashboardData?.expiringItems ?? [];
}

const DASHBOARD_STAGGER = {
    greeting: 0,
    vitals: 1,
    recipeTitle: 2,
    recipeCard: 3,
    hitTitle: 4,
    hitList: 5,
} as const;

function applyCascadeFade(el: HTMLElement, stagger: number): void {
    el.classList.add("cascade-fade");
    el.style.setProperty("--stagger", String(stagger));
}

function applyCascadeGlide(el: HTMLElement, stagger: number): void {
    el.classList.add("cascade-glide");
    el.style.setProperty("--stagger", String(stagger));
}

interface DashboardShell {
    greetingEl: HTMLElement;
    vitalsLedger: HTMLElement;
    vitalsDataRow: HTMLElement;
    vitalsQuip: HTMLElement;
    recipeCard: HTMLElement;
    hitListMount: HTMLElement;
}

function mountDashboardShell(container: HTMLElement): DashboardShell {
    container.innerHTML = "";

    const root = document.createElement("div");
    root.className = "dashboard view-enter";

    const greetingEl = document.createElement("section");
    greetingEl.className = "greeting-masthead cascade-fade";
    greetingEl.style.setProperty("--stagger", String(DASHBOARD_STAGGER.greeting));
    greetingEl.innerHTML = `
    <p class="greeting-date">${formatDiaryDate()}</p>
    <h1 class="greeting-headline loading-shimmer" aria-busy="true">&nbsp;</h1>
    <p class="greeting-line loading-shimmer" aria-busy="true">&nbsp;</p>
  `;

    const vitalsLedger = document.createElement("div");
    vitalsLedger.className = "vitals-ledger cascade-glide";
    vitalsLedger.style.setProperty("--stagger", String(DASHBOARD_STAGGER.vitals));

    const vitalsDataRow = document.createElement("div");
    vitalsDataRow.className = "vitals-data-row vitals-data-row--loading";
    vitalsDataRow.innerHTML =
        '<div class="vitals-stats-shimmer loading-shimmer" aria-hidden="true"></div>';

    const vitalsQuip = document.createElement("p");
    vitalsQuip.className = "vitals-quip vitals-quip--loading loading-shimmer";
    vitalsQuip.setAttribute("aria-hidden", "true");

    vitalsLedger.appendChild(vitalsDataRow);
    vitalsLedger.appendChild(vitalsQuip);

    const recipeSection = document.createElement("section");
    const recipeSectionTitle = document.createElement("h2");
    recipeSectionTitle.className = "dashboard-section-title cascade-fade";
    recipeSectionTitle.style.setProperty("--stagger", String(DASHBOARD_STAGGER.recipeTitle));
    recipeSectionTitle.textContent = msg("dashboard/recipe-section-title");

    const recipeCard = document.createElement("div");
    recipeCard.className = "recipe-card recipe-card--loading cascade-glide";
    recipeCard.style.setProperty("--stagger", String(DASHBOARD_STAGGER.recipeCard));
    recipeCard.innerHTML =
        '<div class="recipe-card-shimmer loading-shimmer" aria-hidden="true"></div>';

    recipeSection.appendChild(recipeSectionTitle);
    recipeSection.appendChild(recipeCard);

    const hitSection = document.createElement("section");
    const hitSectionTitle = document.createElement("h2");
    hitSectionTitle.className = "dashboard-section-title cascade-fade";
    hitSectionTitle.style.setProperty("--stagger", String(DASHBOARD_STAGGER.hitTitle));
    hitSectionTitle.textContent = msg("dashboard/hit-list-section-title");

    const hitListMount = document.createElement("div");
    hitListMount.className = "hit-list-mount";
    hitListMount.innerHTML = `
    <div class="dashboard-shimmer-hit-sheet" aria-hidden="true">
      <div class="dashboard-shimmer-hit-row loading-shimmer cascade-glide" style="--stagger:${DASHBOARD_STAGGER.hitList}"></div>
      <div class="dashboard-shimmer-hit-row loading-shimmer cascade-glide" style="--stagger:${DASHBOARD_STAGGER.hitList + 1}"></div>
      <div class="dashboard-shimmer-hit-row loading-shimmer cascade-glide" style="--stagger:${DASHBOARD_STAGGER.hitList + 2}"></div>
    </div>
  `;

    hitSection.appendChild(hitSectionTitle);
    hitSection.appendChild(hitListMount);

    root.appendChild(greetingEl);
    root.appendChild(vitalsLedger);
    root.appendChild(recipeSection);
    root.appendChild(hitSection);
    container.appendChild(root);

    return { greetingEl, vitalsLedger, vitalsDataRow, vitalsQuip, recipeCard, hitListMount };
}

function ensureDashboardData(): DashboardData {
    if (!dashboardData) {
        dashboardData = {
            greeting: { headline: "", line: "" },
            greetingSlot: "",
            greetingCached: false,
            vital: { value: 0, label: "" },
            vitalCached: false,
            stats: { totalItems: 0, expiredCount: 0, expiringSoonCount: 0, outOfStock: 0 },
            recipe: null,
            recipeCached: false,
            candidateIds: [],
            expiringItems: [],
        };
    }
    return dashboardData;
}

function showGreetingError(section: HTMLElement): void {
    const headlineEl = section.querySelector(".greeting-headline")!;
    const lineEl = section.querySelector(".greeting-line")!;
    headlineEl.classList.remove("loading-shimmer");
    headlineEl.removeAttribute("aria-busy");
    headlineEl.textContent = msg("dashboard/server-unreachable");
    lineEl.classList.remove("loading-shimmer");
    lineEl.removeAttribute("aria-busy");
    lineEl.classList.add("hidden");
}

function showVitalsStatsError(row: HTMLElement): void {
    row.classList.remove("vitals-data-row--loading");
    row.replaceChildren();
    const err = document.createElement("p");
    err.className = "dashboard-section-error";
    err.textContent = msg("app/server-unreachable");
    row.appendChild(err);
}

function showVitalQuipError(quip: HTMLElement): void {
    quip.classList.remove("loading-shimmer", "vitals-quip--loading");
    quip.removeAttribute("aria-hidden");
    quip.textContent = "";
}

function showRecipeError(card: HTMLElement): void {
    card.classList.remove("recipe-card--loading");
    card.replaceChildren();
    const err = document.createElement("p");
    err.className = "recipe-empty";
    err.textContent = msg("dashboard/recipe-server-offline");
    card.appendChild(err);
}

export async function renderDashboard(container: HTMLElement, gen: number): Promise<void> {
    if (isNavigationStale(gen)) return;

    window.addEventListener("pantry-sync", () => {
        if (isNavigationStale(gen)) return;
        void api.dashboard.getStats().then((data) => {
            if (isNavigationStale(gen)) return;
            const vitalsRow = container.querySelector<HTMLElement>(".vitals-data-row");
            const hitList = container.querySelector<HTMLElement>(".hit-list-mount");
            if (vitalsRow) renderVitalsStatsRow(vitalsRow, data.stats);
            if (hitList) renderHitList(hitList, data.expiringItems, { animateEntrance: false });
        }).catch(() => {});

        void api.dashboard.getGreeting().then((data) => {
            if (isNavigationStale(gen)) return;
            const state = ensureDashboardData();
            state.greeting = data.greeting;
            state.greetingSlot = data.greetingSlot;
            state.greetingCached = data.greetingCached;
            renderGreeting(shell.greetingEl, data.greeting);
        }).catch(() => {
            if (isNavigationStale(gen)) return;
            showGreetingError(shell.greetingEl);
        });

        void api.dashboard.getVital().then((data) => {
            if (isNavigationStale(gen)) return;
            ensureDashboardData().vital = data.vital;
            renderVitalQuip(shell.vitalsQuip, data.vital);
        }).catch(() => {
            if (isNavigationStale(gen)) return;
            showVitalQuipError(shell.vitalsQuip);
        });

        void api.dashboard.getRecipe().then((data) => {
            if (isNavigationStale(gen)) return;
            const state = ensureDashboardData();
            state.recipe = data.recipe;
            state.recipeCached = data.recipeCached;
            state.candidateIds = data.candidateIds;
            shell.recipeCard.classList.remove("recipe-card--loading");
            renderRecipeCard(shell.recipeCard, data.recipe, container);
        }).catch(() => {
            if (isNavigationStale(gen)) return;
            showRecipeError(shell.recipeCard);
        });
    });

    dashboardData = null;
    const shell = mountDashboardShell(container);

    void api.dashboard
        .getStats()
        .then((data) => {
            if (isNavigationStale(gen)) return;
            const state = ensureDashboardData();
            state.stats = data.stats;
            state.expiringItems = data.expiringItems;
            renderVitalsStatsRow(shell.vitalsDataRow, data.stats);
            renderHitList(shell.hitListMount, data.expiringItems, { animateEntrance: true });
        })
        .catch(() => {
            if (isNavigationStale(gen)) return;
            showVitalsStatsError(shell.vitalsDataRow);
            shell.hitListMount.replaceChildren();
            const err = document.createElement("p");
            err.className = "hit-list-empty";
            err.textContent = msg("dashboard/hit-list-no-data");
            shell.hitListMount.appendChild(err);
        });

    void api.dashboard
        .getVital()
        .then((data) => {
            if (isNavigationStale(gen)) return;
            ensureDashboardData().vital = data.vital;
            renderVitalQuip(shell.vitalsQuip, data.vital);
        })
        .catch(() => {
            if (isNavigationStale(gen)) return;
            showVitalQuipError(shell.vitalsQuip);
        });

    void api.dashboard
        .getGreeting()
        .then((data) => {
            if (isNavigationStale(gen)) return;
            const state = ensureDashboardData();
            state.greeting = data.greeting;
            state.greetingSlot = data.greetingSlot;
            state.greetingCached = data.greetingCached;
            renderGreeting(shell.greetingEl, data.greeting);
        })
        .catch(() => {
            if (isNavigationStale(gen)) return;
            showGreetingError(shell.greetingEl);
        });

    void api.dashboard
        .getRecipe()
        .then((data) => {
            if (isNavigationStale(gen)) return;
            const state = ensureDashboardData();
            state.recipe = data.recipe;
            state.recipeCached = data.recipeCached;
            state.candidateIds = data.candidateIds;
            shell.recipeCard.classList.remove("recipe-card--loading");
            renderRecipeCard(shell.recipeCard, data.recipe, container);
        })
        .catch(() => {
            if (isNavigationStale(gen)) return;
            showRecipeError(shell.recipeCard);
        });
}

function renderGreeting(section: HTMLElement, greeting: DashboardGreeting): void {
    const headlineEl = section.querySelector(".greeting-headline")!;
    const lineEl = section.querySelector(".greeting-line")!;
    headlineEl.classList.remove("loading-shimmer");
    headlineEl.removeAttribute("aria-busy");
    headlineEl.textContent = greeting.headline;
    lineEl.classList.remove("loading-shimmer");
    lineEl.removeAttribute("aria-busy");
    lineEl.textContent = greeting.line;
    lineEl.classList.toggle("hidden", !greeting.line);
}

function renderVitalsStatsRow(row: HTMLElement, stats: DashboardData["stats"]): void {
    row.classList.remove("vitals-data-row--loading");
    row.replaceChildren();
    row.className = "vitals-data-row";

    const statsConfig = [
        {
            label: msg("dashboard/vitals-in-stock"),
            value: stats.totalItems,
            tone: "default" as const,
            alert: false,
            href: "#/pantry?reset=1&stocked=1",
        },
        {
            label: msg("dashboard/vitals-expired"),
            value: stats.expiredCount,
            tone: "expired" as const,
            alert: stats.expiredCount > 0,
            href: "#/pantry?reset=1&expired=1",
        },
        {
            label: msg("dashboard/vitals-expiring-soon"),
            value: stats.expiringSoonCount,
            tone: "expiring" as const,
            alert: stats.expiringSoonCount > 0,
            href: "#/pantry?reset=1&expiring=7",
        },
        {
            label: msg("dashboard/vitals-to-buy"),
            value: stats.outOfStock,
            tone: "default" as const,
            alert: stats.outOfStock > 0,
            href: "#/grocery",
        },
    ];

    for (let i = 0; i < statsConfig.length; i++) {
        const stat = statsConfig[i];
        const el = document.createElement("a");
        const toneClass =
            stat.tone === "expired"
                ? " vitals-stat--expired"
                : stat.tone === "expiring"
                  ? " vitals-stat--expiring"
                  : "";
        el.className = `vitals-stat${toneClass}${stat.alert ? " vitals-stat--alert" : ""}`;
        if (i < statsConfig.length - 1) el.classList.add("vitals-stat--ruled");
        el.href = stat.href;
        el.setAttribute("aria-label", `${stat.value} ${stat.label}`);
        el.innerHTML = `
      <span class="vitals-stat-value">${stat.value}</span>
      <span class="vitals-stat-label">${stat.label}</span>
    `;
        row.appendChild(el);
    }
}

function renderVitalQuip(quip: HTMLElement, vital: DashboardData["vital"]): void {
    quip.classList.remove("loading-shimmer", "vitals-quip--loading");
    quip.removeAttribute("aria-hidden");
    quip.textContent = `${vital.value} ${vital.label}.`;
}

function waitTransition(el: HTMLElement, fallbackMs: number): Promise<void> {
    return new Promise((resolve) => {
        let settled = false;
        const finish = () => {
            if (settled) return;
            settled = true;
            el.removeEventListener("transitionend", onEnd);
            resolve();
        };
        const onEnd = (e: TransitionEvent) => {
            if (e.target === el) finish();
        };
        el.addEventListener("transitionend", onEnd);
        window.setTimeout(finish, fallbackMs);
    });
}

function populateRecipeContent(container: HTMLElement, recipe: RecipeSuggestion): void {
    container.replaceChildren();

    const dish = document.createElement("h2");
    dish.className = "recipe-dish";
    dish.textContent = recipe.dish;

    const ingredientsLine = document.createElement("p");
    ingredientsLine.className = "recipe-ingredients-line";
    ingredientsLine.appendChild(renderIngredientsLine(recipe, getExpiringItems()));

    const cta = document.createElement("button");
    cta.type = "button";
    cta.className = "recipe-cta-link";
    cta.textContent = msg("dashboard/recipe-cta");
    cta.addEventListener("click", () => {
        openRecipeSheet(recipe, getExpiringItems());
    });

    container.appendChild(dish);
    container.appendChild(ingredientsLine);
    container.appendChild(cta);
}

function renderRecipeCard(
    card: HTMLElement,
    recipe: RecipeSuggestion | null,
    container: HTMLElement,
): void {
    card.replaceChildren();

    if (!recipe) {
        card.innerHTML = `<p class="recipe-empty">${msg("dashboard/recipe-empty")}</p>`;
        return;
    }

    const spinBtn = document.createElement("button");
    spinBtn.type = "button";
    spinBtn.className = "recipe-spin-btn";
    spinBtn.setAttribute("aria-label", navigator.onLine ? "Spin again" : "Offline");
    spinBtn.disabled = !navigator.onLine;
    spinBtn.appendChild(createIcon(navigator.onLine ? "casino" : "cloud_off"));
    spinBtn.addEventListener("click", () => void spinRecipe(card, spinBtn, container));

    const content = document.createElement("div");
    content.className = "recipe-card-content";
    populateRecipeContent(content, recipe);

    card.appendChild(spinBtn);
    card.appendChild(content);
}

async function spinRecipe(
    card: HTMLElement,
    spinBtn: HTMLButtonElement,
    _container: HTMLElement,
): Promise<void> {
    const content = card.querySelector(".recipe-card-content") as HTMLElement | null;
    if (!content) return;

    spinBtn.classList.add("spinning");
    spinBtn.disabled = true;
    content.classList.remove("is-entering");
    content.classList.add("is-exiting");

    try {
        const [, result] = await Promise.all([
            waitTransition(content, 280),
            api.dashboard.spinRecipe(),
        ]);

        if (dashboardData) dashboardData.recipe = result.recipe;

        if (!result.recipe) {
            content.replaceChildren();
            const empty = document.createElement("p");
            empty.className = "recipe-empty";
            empty.textContent = msg("dashboard/recipe-empty");
            content.appendChild(empty);
        } else {
            populateRecipeContent(content, result.recipe);
        }

        content.classList.remove("is-exiting");
        content.classList.add("is-entering");
        content.addEventListener("animationend", () => content.classList.remove("is-entering"), {
            once: true,
        });
    } catch {
        content.classList.remove("is-exiting");
        content.classList.add("is-entering");
        content.addEventListener("animationend", () => content.classList.remove("is-entering"), {
            once: true,
        });
    } finally {
        spinBtn.classList.remove("spinning");
        spinBtn.disabled = false;
    }
}

function renderHitList(
    mount: HTMLElement,
    items: ExpiringItemView[],
    options: { animateEntrance?: boolean } = {},
): void {
    mount.innerHTML = "";
    const { animateEntrance = false } = options;

    if (items.length === 0) {
        const empty = document.createElement("p");
        empty.className = "hit-list-empty";
        empty.textContent = msg("dashboard/hit-list-empty");
        if (animateEntrance) applyCascadeGlide(empty, DASHBOARD_STAGGER.hitList);
        mount.appendChild(empty);
        return;
    }

    const sheet = document.createElement("div");
    sheet.className = "hit-list-sheet";
    if (animateEntrance) applyCascadeGlide(sheet, DASHBOARD_STAGGER.hitList);

    for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const card = document.createElement("div");
        card.className = "hit-list-card";
        card.dataset.itemId = String(item.id);
        card.style.cursor = "pointer";
        card.addEventListener("click", () => {
            location.hash = `#/pantry?reset=1&item=${item.id}`;
        });

        const countdown = formatEditorialCountdown(item.daysLeft);
        const countdownCol = document.createElement("div");
        const countdownTone = countdown.expired
            ? " hit-list-countdown-col--expired"
            : item.daysLeft !== null && item.daysLeft <= 7
              ? " hit-list-countdown-col--expiring"
              : "";
        countdownCol.className = `hit-list-countdown-col${countdownTone}`;
        countdownCol.innerHTML = `
      <span class="hit-list-countdown-value">${countdown.value}</span>
      <span class="hit-list-countdown-unit">${countdown.unit}</span>
    `;

        const divider = document.createElement("div");
        divider.className = "hit-list-divider-v";
        divider.setAttribute("aria-hidden", "true");

        const body = document.createElement("div");
        body.className = "hit-list-body";

        const thumb = document.createElement("div");
        thumb.className = "hit-list-thumb hit-list-thumb--round";
        if (item.image_path) {
            const img = document.createElement("img");
            void resolveCachedImageSrc(item.image_path).then((src) => {
                if (src) img.src = src;
            });
            img.alt = "";
            thumb.appendChild(img);
        } else {
            thumb.appendChild(createIcon("inventory_2"));
        }

        const info = document.createElement("div");
        info.className = "hit-list-info";
        const name = document.createElement("div");
        name.className = "hit-list-name";
        name.textContent = item.name;
        const loc = document.createElement("div");
        loc.className = "hit-list-location";
        loc.textContent = item.location_name ?? "";
        info.appendChild(name);
        info.appendChild(loc);

        body.appendChild(thumb);
        body.appendChild(info);

        card.appendChild(countdownCol);
        card.appendChild(divider);
        card.appendChild(body);

        sheet.appendChild(card);
    }

    mount.appendChild(sheet);
}
